import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';

import { ProgramacionPendiente } from 'src/app/domain/model/cumplimiento/cumplimiento';
import { ESTADO_CITA } from 'src/app/common/constants-ejb';
import { tablaProgramacionesPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * DUMB COMPONENT: TablaProgramaciones
 *
 * Muestra la tabla de programaciones pendientes con paginación manual.
 * Solo presenta datos, no tiene lógica de negocio.
 *
 * Migrado de: FilterOrdenesProgViewImpl.java (grid de resultados)
 *
 * @example
 * <app-tabla-programaciones
 *   [data]="paginatedData()"
 *   [loading]="loading()"
 *   [totalRegistros]="totalRegistros()"
 *   [currentPage]="currentPage()"
 *   [totalPages]="totalPages()"
 *   [pageSize]="pageSize()"
 *   [registroInicio]="registroInicio()"
 *   [registroFin]="registroFin()"
 *   [hasPrevious]="hasPrevious()"
 *   [hasNext]="hasNext()"
 *   (seleccionar)="onSeleccionar($event)"
 *   (cambiarPagina)="onCambiarPagina($event)"
 *   (cambiarTamanio)="onCambiarTamanio($event)" />
 */
@Component({
  selector: 'app-tabla-programaciones',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    CardModule,
    DropdownModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-card class="mb-4">
      <ng-template pTemplate="header">
        <div
          class="p-3 pb-0 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2"
        >
          <h3 class="text-lg font-semibold text-gray-700 m-0">
            Programaciones Pendientes
          </h3>
          @if (totalRegistros() > 0) {
            <span class="text-sm text-gray-500"
              >Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalRegistros() }}</span
            >
          }
        </div>
      </ng-template>

      @if (data().length > 0) {
        <div class="md:hidden space-y-3">
          @for (prog of data(); track prog.nmconsecutivoManualOrden ?? $index) {
            <article
              class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <h3
                  class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                >
                  Orden {{ textoTabla(prog.nmconsecutivoManualOrden) }}
                </h3>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  {{ textoTabla(formatFecha(prog.feprogramacion ?? null)) }}
                </span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Tipo:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(prog.dstipoCita || prog.dstipo)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Aseguradora:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(prog.dsnombreAseguradora)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Cliente:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(prog.dsnombreCliente)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Centro Trabajo:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(prog.dscentroTrabajo)
                  }}</span>
                </div>
                <div class="sm:col-span-2">
                  <span class="text-gray-500 dark:text-gray-400">Tarea:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(prog.dstarea)
                  }}</span>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-2">
                <p-button
                  label="Registrar Cumplimiento"
                  icon="pi pi-calendar-plus"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="seleccionar.emit(prog)"
                />
              </div>
            </article>
          }
        </div>
      }

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="programacionesTableConfig().a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="data()"
          [paginator]="false"
          [rows]="pageSize()"
          [loading]="loading()"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{
            'min-width':
              programacionesTableConfig().responsive.minWidth || '92rem'
          }"
          [styleClass]="programacionesTableStyleClass()"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn style="width: 100px">
                <span class="grid-header-text" title="Orden">Orden</span>
              </th>
              <th pResizableColumn style="width: 80px">
                <span class="grid-header-text" title="Tipo">Tipo</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Aseguradora"
                  >Aseguradora</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Cliente">Cliente</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Centro Trabajo"
                  >Centro Trabajo</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Tarea">Tarea</span>
              </th>
              <th pResizableColumn style="width: 110px">
                <span class="grid-header-text" title="Fecha Programación"
                  >Fecha Prog.</span
                >
              </th>
              <th pResizableColumn style="width: 90px">
                <span class="grid-header-text" title="Acciones">Acciones</span>
              </th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-prog>
            <tr>
              <td class="font-mono">
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(prog.nmconsecutivoManualOrden)"
                  >{{ textoTabla(prog.nmconsecutivoManualOrden) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(prog.dstipoCita || prog.dstipo)"
                  >{{ textoTabla(prog.dstipoCita || prog.dstipo) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(prog.dsnombreAseguradora)"
                  >{{ textoTabla(prog.dsnombreAseguradora) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(prog.dsnombreCliente)"
                  >{{ textoTabla(prog.dsnombreCliente) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(prog.dscentroTrabajo)"
                  >{{ textoTabla(prog.dscentroTrabajo) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(prog.dstarea)"
                  >{{ textoTabla(prog.dstarea) }}</span
                >
              </td>
              <td class="text-center">
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(formatFecha(prog.feprogramacion ?? null))
                  "
                  >{{
                    textoTabla(formatFecha(prog.feprogramacion ?? null))
                  }}</span
                >
              </td>
              <td class="text-center">
                <button
                  pButton
                  icon="pi pi-calendar-plus"
                  class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                  (click)="seleccionar.emit(prog)"
                  pTooltip="Registrar Cumplimiento"
                ></button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="8" class="text-center p-6 text-gray-500">
                No se encontraron programaciones.
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Paginación -->
      @if (totalRegistros() > 0) {
        <app-busqueda-paginador
          [currentPage]="currentPage()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptionValues"
          [selectedPageSize]="pageSize()"
          (pageSizeChange)="cambiarTamanio.emit($event)"
          (first)="cambiarPagina.emit('first')"
          (previous)="cambiarPagina.emit('prev')"
          (next)="cambiarPagina.emit('next')"
          (last)="cambiarPagina.emit('last')"
        ></app-busqueda-paginador>
      }
    </p-card>
  `
})
export class TablaProgramacionesComponent {
  // Inputs de datos
  data = input<ProgramacionPendiente[]>([]);
  loading = input<boolean>(false);

  // Inputs de paginación
  totalRegistros = input<number>(0);
  currentPage = input<number>(1);
  totalPages = input<number>(1);
  pageSize = input<number>(10);
  registroInicio = input<number>(0);
  registroFin = input<number>(0);
  hasPrevious = input<boolean>(false);
  hasNext = input<boolean>(false);

  // Outputs
  seleccionar = output<ProgramacionPendiente>();
  cambiarPagina = output<'first' | 'prev' | 'next' | 'last'>();
  cambiarTamanio = output<number>();

  // Opciones de paginación
  readonly pageSizeOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 }
  ];

  readonly pageSizeOptionValues = this.pageSizeOptions.map(
    (item) => item.value
  );

  readonly programacionesTableConfig = computed(() =>
    tablaProgramacionesPreset.createConfig({
      data: this.data() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-cumplimientos-programados',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      pagination: {
        enabled: false,
        mode: 'server',
        pageSize: this.pageSize(),
        pageSizeOptions: this.pageSizeOptions.map((item) => item.value),
        currentPageReportTemplate:
          'Registros del {first} hasta el {last} de {totalRecords}'
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron programaciones.'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '92rem',
        scrollHeight: 'auto',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de programaciones pendientes',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly programacionesTableStyleClass = computed(() => {
    const config = this.programacionesTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Helpers de formato - usan constantes del legacy (constants-ejb.ts)
  formatFecha(fecha: string | Date | null): string {
    if (!fecha) return '';
    let d: Date;
    if (typeof fecha === 'string') {
      // Si viene como YYYY-MM-DD, parsear manualmente para evitar problema de UTC
      // new Date("2026-05-21") interpreta como UTC, causando desfase de 1 día en timezone local
      if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        const [year, month, day] = fecha.split('-').map(Number);
        d = new Date(year, month - 1, day);
      } else {
        d = new Date(fecha);
      }
    } else {
      d = fecha;
    }
    // Legacy usa barras: dd/MM/yyyy
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  /**
   * Mapea código de estado a severity de PrimeNG Tag
   * Legacy: TPSC_ESTADOS - códigos '0015', '0016', '0017', '0019'
   */
  getEstadoSeverity(
    cdestado: string
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const map: Record<
      string,
      'success' | 'info' | 'warn' | 'danger' | 'secondary'
    > = {
      [ESTADO_CITA.PROGRAMADA]: 'info', // 0015 - Programado
      [ESTADO_CITA.EJECUTADA]: 'success', // 0016 - Ejecutado
      [ESTADO_CITA.CANCELADA]: 'danger', // 0017 - Cancelado
      [ESTADO_CITA.RESERVADA]: 'warn' // 0019 - Reservado
    };
    return map[cdestado] || 'secondary';
  }
}
